// / ///////////////////////////////////////////////////////////////////////////
// Copyright (c) 2005-2015 inSORS Integrated Communications, All Rights Reserved
// COMPANY CONFIDENTIAL
// This file may not be reproduced or transmitted without explicit permission
//
// $File: //iocommain/IGWebClient/js/webrtc_client.js $
//
// Perforce RCS keywords (add with -t text+k to use these)
// $Author: dswan $
// $DateTime: 2018/05/09 17:16:10 $
// $Revision: #85 $
// $Change: 54019 $
// / ////////////////////////////////////////////////////////////////////////////

import $ from "jquery";

import BridgeNode from "../bridge_node";
import { createBlurEffect, stopBlurEffect } from "../videoEffects";

export default class WebrtcClient {
  constructor(options) {
    const defaults = {
      encryption_key: "dtls",
      mode: "sendrecv",
      resolution: "1280x720",
      ucg: false,
      completed_callback() {},
      error_callback() {},
      screen: false,
      onended() {},
      blur: false,
      maxBandwidth: 3756, // 3.756mbps
    };

    Object.assign(this, defaults, options);

    this.log = (str) => {
      const logPrefix =
        this.type && this.mode ? `${this.type} - ${this.mode}: ` : "";

      if (options.log) {
        options.log(logPrefix + str);
      }

      if (window.visionable_debug) {
        console.log(logPrefix + str);
      }
    };

    this.log("WebrtcClient");
    this.log(options);

    this.isSafari =
      navigator.userAgent.indexOf("Safari") !== -1 &&
      navigator.userAgent.indexOf("Chrome") === -1;
    this.isAndroidChrome = !!navigator.userAgent.match(
      /Android.*Chrome.*Mobile/
    );
    this.isAndroid = !!navigator.userAgent.match(/Android.*Mobile/);

    this.error = null;
    this.started = false;
    this.is_audio = this.type === "audio";
    this.is_dtls = this.encryption_key === "dtls";
    this.bandwidth = {
      audio: this.max_bandwidth || 50,
      video: this.max_bandwidth || 1000,
    };

    if (this.mode !== "none") {
      this.configure();
    }

    this.getServerForMeeting(() => {
      this.doGetUserMedia();
    });
  }

  // TODO: remove for v3
  getServerForMeeting(callback = () => {}) {
    if (this.ucg) {
      const url = `https://${this.server}/next_available_for_meeting/${
        this.is_audio ? 4 : 6
      }/${this.meetingID}`;

      $.ajax({
        type: "GET",
        url,
        contentType: "text/csv",
        timeout: 12000,
        success: (server) => {
          this.server = server;
          callback();
        },
        error: callback,
      });
    } else {
      callback();
    }
  }

  configure() {
    this.sdpConstraints = {
      offerToReceiveAudio: this.is_audio && this.mode === "recvonly",
      offerToReceiveVideo: !this.is_audio && this.mode === "recvonly",
    };

    let videoProperties = false;

    if (!this.is_audio) {
      this.video_width = this.resolution?.split("x")[0];
      this.video_height = this.resolution?.split("x")[1];

      videoProperties = {};

      if (this.mode === "sendonly") {
        videoProperties.width = { ideal: this.video_width };
        videoProperties.height = { ideal: this.video_height };
      }

      if (this.facingMode) {
        videoProperties.facingMode = {
          ideal: this.facingMode,
        };
      } else if (this.device_id && this.device_id !== "default") {
        videoProperties.deviceId = {
          exact: this.device_id,
        };
      }
    }

    this.mediaConstraints = {
      audio: this.is_audio,
      video: videoProperties,
    };
  }

  startRtsp() {
    this.bridge_client.Setup(
      (res, status, xhr) => {
        this.bridge_client.ExchangeSdp(
          this.modifySdp(this.pc.localDescription.sdp),
          (data) => {
            if (data) {
              this.log(`Remote SDP: ${data}`);
              const sdpAnswer = { sdp: data, type: "answer" };
              this.sdpAnswer = data.trim();

              if (this.type === "video" && this.mode === "recvonly") {
                // save this for when we reconstruct the SDP later
                this.mapNumber =
                  this.sdpAnswer.match(/^m=video.*\s(\d+)$/m)?.[1];
              }

              if (this.pc) {
                if (this.mode === "sendonly" && this.type === "video") {
                  this.setBandwidthLimit(
                    getMaxBandwidth(this.video_width, this.video_height)
                      .maxBandwidth
                  );
                }
                this.pc.setRemoteDescription(
                  sdpAnswer,
                  () => {
                    this.log("setRemoteDescription success");

                    if (this.is_audio && this.mode === "recvonly") {
                      this.bridge_client.CreateStream("0", () => {
                        this.log("Created stream");
                        this.completed_callback(res, status, xhr, this.error); // Let the layer above know we are complete
                      });
                    } else {
                      this.completed_callback(res, status, xhr, this.error); // Let the layer above know we are complete
                    }
                  },
                  (error) => {
                    this.log(`setRemoteDescription failure: ${error}`, "error");
                  }
                );
              }
            } else {
              this.log("No remote SDP received", "error");
            }
          },
          () => {
            this.log("Error exchanging SDP", "error");
          }
        );
      },
      (a, b, c) => {
        this.log("Error setting up BridgeClient", "error");
        this.error_callback(a, b, c);
      }
    );
  }

  setMediaBitrate(sdp, mediaType, bitrate) {
    const sdpLines = sdp.split("\n");
    let mediaLineIndex = -1;
    const mediaLine = `m=${mediaType}`;
    let bitrateLineIndex = -1;
    const bitrateLine = `b=${
      navigator.userAgent.includes("Firefox") ? "TIAS" : "AS"
    }:${bitrate}`;
    mediaLineIndex = sdpLines.findIndex((line) => line.startsWith(mediaLine));

    // If we find a line matching “m={mediaType}”
    if (mediaLineIndex && mediaLineIndex < sdpLines.length) {
      // Skip the media line
      bitrateLineIndex = mediaLineIndex + 1;

      // Skip both i=* and c=* lines (bandwidths limiters have to come afterwards)
      while (
        sdpLines[bitrateLineIndex].startsWith("i=") ||
        sdpLines[bitrateLineIndex].startsWith("c=")
      ) {
        bitrateLineIndex++;
      }

      if (sdpLines[bitrateLineIndex].startsWith("b=")) {
        // If the next line is a b=* line, replace it with our new bandwidth
        sdpLines[bitrateLineIndex] = bitrateLine;
      } else {
        // Otherwise insert a new bitrate line.
        sdpLines.splice(bitrateLineIndex, 0, bitrateLine);
      }
    }

    // Then return the updated sdp content as a string
    return sdpLines.join("\n");
  }

  modifySdp(oldSdp) {
    let sdp = oldSdp.slice();

    sdp = sdp.replace(/\r\n/g, "\n");
    sdp = sdp.replace(/sendrecv/g, this.mode);

    sdp += `a=email:${this.email}\n`;
    sdp += `a=site-name:${this.site}\n`;

    // Don't offer RFC 6464 audio level indication
    sdp = sdp.replace(
      /a=extmap:\d+ urn:ietf:params:rtp-hdrext:ssrc-audio-level.*\n/,
      ""
    );

    if (this.device_name) {
      sdp += `a=device-name:${this.device_name}\n`;
    }
    if (this.video_width) {
      sdp += `a=video-width:${this.video_width}\n`;
    }
    if (this.video_height) {
      sdp += `a=video-height:${this.video_height}\n`;
    }
    if (this.screen) {
      const codecName = "Screen Share";
      sdp += `a=codec-name:${codecName}\n`;
    }
    if (this.mode === "recvonly") {
      sdp = sdp.replace(/a=ssrc.*\n/g, "");
    }
    sdp = sdp.replace(/a=candidate.*\n/g, ""); // remove whatever candidates it came up with
    sdp += "a=candidate:0 1 UDP 2122252543 192.168.0.1 57178 typ host\n"; // and just put a "dummy" IPv4 one in! We don't use 'em anyway

    if (navigator && navigator.vendor === "Apple Computer, Inc.") {
      // Safari, so the bridges knows to use the worse cypher
      sdp += "a=visi-safari:true\n";
    }

    return sdp;
  }

  justPerformRtsp() {
    this.createBridgeClient();
    this.bridge_client.Setup(this.completed_callback, this.error_callback);
  }

  createBridgeClient() {
    this.bridge_client = new BridgeNode(
      this.server,
      this.type,
      this.meetingID,
      this.email,
      this.notification_callback,
      this.log,
      this.mjwt
    );
  }

  doGetUserMedia() {
    this.log("doGetUserMedia");

    if (this.mode !== "none") {
      try {
        if (this.mode === "sendonly" || this.mode === "sendrecv") {
          const mediaTypeMethod = this.screen
            ? "getDisplayMedia"
            : "getUserMedia";
          const constraints = this.screen ? {} : this.mediaConstraints;

          // if this is sending, then get Media
          navigator.mediaDevices[mediaTypeMethod](constraints)
            .then((stream) => {
              this.log("getUserMedia success");

              if (this.is_audio) {
                window.IOCOMGotAudioUserMedia = stream; // store it away in a global
                this.audioStream = stream;

                try {
                  // hook up level meters
                  const audioContext = new AudioContext();
                  const mediaStreamSource =
                    audioContext.createMediaStreamSource(stream);
                  this.audioInputMeter = audioMeter(audioContext);
                  mediaStreamSource.connect(this.audioInputMeter);
                } catch (e) {
                  this.log("error enabling audio level meters");
                }
              }

              if (this.mode === "sendonly" || this.mode === "sendrecv") {
                this.onUserMediaSuccess(stream);
              } else if (this.mode === "recvonly") {
                this.start();
              }
            })
            .catch((error) => {
              this.log("getUserMedia error", "error");
              this.log(error, "error");
              this.onUserMediaError(error);
            });

          this.log("Requested access to local media");
          this.log(JSON.stringify(this.mediaConstraints));
        } else {
          // video recvonly
          this.log("Skipping getUserMedia since this is recvonly");
          this.start();
        }
      } catch (e) {
        this.log(`getUserMedia failed with exception: ${e.message}`, "error");
      }
    } else {
      this.justPerformRtsp();
    }
  }

  createPeerConnection() {
    this.log("createPeerConnection");

    try {
      this.pc = new RTCPeerConnection();

      this.pc.oniceconnectionstatechange = () => {
        this.log("oniceconnectionstatechange");
        this.log(this.pc.iceConnectionState);
        /*
        if (this.mode === 'sendonly') {
          if (this.pc.iceConnectionState === 'failed' || this.pc.iceConnectionState === 'disconnected') {
            // const mediaType = this.is_audio ? 'Audio' : 'Video';
            // window.top_banner.add(`${mediaType} connection ${this.pc.iceConnectionState}`, { life: 5000, closeable: true, type: 'error' });
          }
        }
        */
      };
      this.pc.ontrack = (t) => this.onRemoteStreamAdded(t);
      this.pc.onremovestream = (s) => this.onRemoteStreamRemoved(s);
      this.log("Created RTCPeerConnection");
    } catch (e) {
      this.log(`Failed to create PeerConnection: ${e.message}`);
      return false;
    }

    this.log("Adding local stream");
    if (this.mode === "sendonly" || this.mode === "sendrecv") {
      this.stream.getTracks().forEach((track) => {
        this.pc.addTrack(track, this.stream);
      });
    }

    this.started = true;

    return true;
  }

  start() {
    this.createBridgeClient();

    if (this.started) {
      this.log("Failed to start WebrtcClient", "error");
      return;
    }

    if (!this.createPeerConnection()) {
      return;
    }

    this.pc
      .createOffer(this.sdpConstraints)
      .then((offer) => {
        const { sdp } = offer;

        this.current_offer = new RTCSessionDescription({
          type: offer.type,
          sdp,
        });

        this.log(`Current SDP: ${sdp}`);

        this.pc.setLocalDescription(
          this.current_offer,
          () => {
            this.log("SetLocalDescription Success");

            this.startRtsp();
          },
          () => {
            this.log("SetLocalDescription Failure", "error");
          }
        );
      })
      .catch((error) => {
        this.log(`Failed to Create Offer: ${error}`);
      });
  }

  async onUserMediaSuccess(stream) {
    this.log("User has granted access to local media");
    $(document).trigger("onUserMedia", ["success"]);

    if (this.mode === "sendonly" || this.mode === "sendrecv") {
      const newStream = await this.getStream(stream);

      this.stream = newStream;
    }

    const that = this;

    if (!this.is_audio && this.mode === "sendonly") {
      const { width, height } = stream.getVideoTracks()?.[0]?.getSettings();

      const track = stream.getVideoTracks()?.[0];
      if (track) {
        track.onended = that.onended;
      }

      that.log(`stream width & height: ${width} x ${height}`);
      that.video_height = height;
      that.video_width = width;

      if (!this.started) {
        that.start();
      }
    } else {
      this.start();
    }
  }

  onUserMediaError(error) {
    $(document).trigger("onUserMedia", ["error", error]);
    this.log(`Failed to get access to local media: ${error.code}`, "error");
    this.error_callback(error);
  }

  onRemoteStreamAdded(event) {
    this.log("Remote stream added");

    if (this.mode === "recvonly" || this.mode === "sendrecv") {
      const stream = event.streams[0];
      this.stream = stream;

      // turn on remote audio
      if (this.is_audio) {
        const ctx = new AudioContext();
        const audio = new Audio();
        audio.srcObject = stream;
        const gainNode = ctx.createGain();
        gainNode.gain.value = 0.5;
        audio.onloadedmetadata = function () {
          const source = ctx.createMediaStreamSource(audio.srcObject);
          audio.play();
          audio.muted = true;
          source.connect(gainNode);
          gainNode.connect(ctx.destination);
        };
      }
    }
  }

  onRemoteStreamRemoved() {
    this.log("Remote stream removed");
  }

  async stop() {
    if (this.stream) {
      const mediaTracks = this.is_audio
        ? this.stream.getAudioTracks()
        : this.stream.getVideoTracks();

      for (let i = 0; i < mediaTracks.length; i++) {
        if (mediaTracks[i].stop) {
          mediaTracks[i].stop();
        }
      }

      if (this.blur) {
        stopBlurEffect(this.device_id);
      }
    }

    this.started = false;
    this.stream = null;

    if (this.pc) {
      this.pc.close();
      this.pc.oniceconnectionstatechange = null;
      this.pc = null;
    }

    if (this.bridge_client) {
      this.bridge_client.NotificationCallback = null;
      try {
        await this.bridge_client.Teardown();
      } catch (e) {
        this.log("Error tearing down bridge");
      }
    }
  }

  turnOn(streamId, turnOnCallback = () => {}) {
    if (this.turningOn) {
      setTimeout(() => {
        this.turnOn(streamId, turnOnCallback);
      }, 200);
      return;
    }
    this.log(`turnon: ${streamId}`);
    this.turningOn = true;
    this.streamId = streamId;

    this.bridge_client.Switch("on", streamId);

    this.addMLine(streamId);

    const newAnswer = { sdp: this.sdpAnswer, type: "offer" };

    this.pc
      .setRemoteDescription(new RTCSessionDescription(newAnswer))
      .then(() => {
        this.log("setRemoteDescription success");
        this.pc
          .createAnswer()
          .then((answer) => {
            this.turningOn = false;
            this.pc.setLocalDescription(answer);
            turnOnCallback(this.stream);
          })
          .catch((e) => {
            this.log(
              "caught error creating answer or setting local description",
              e
            );
            this.turningOn = false;
            turnOnCallback(this.stream);
          });
      })
      .catch((e) => {
        this.log("caught error setting remote description", e);
        this.turningOn = false;
        turnOnCallback(this.stream);
      });
  }

  addMLine(streamId) {
    // only add the M line for a source once
    this.sources = this.sources || {};
    if (this.sources[streamId]) {
      return;
    }
    this.sources[streamId] = true;

    this.lastMid = this.lastMid || 1;
    const mid = this.lastMid;
    this.lastMid++;

    const random = parseInt(Math.random() * 1000000000);

    const bundleMids = Array.from(Array(mid + 1))
      .map((a, i) => i)
      .join(" ");

    this.sdpAnswer = this.sdpAnswer.replace(
      /^o=.*/m,
      `o=visionableSDP 9 ${mid} IN IP4 0.0.0.0`
    );
    this.sdpAnswer = this.sdpAnswer.replace(/^t=.*/m, `t=0 ${mid}`);
    this.sdpAnswer = this.sdpAnswer.replace(
      /^a=group:BUNDLE.*/m,
      `a=group:BUNDLE ${bundleMids}`
    );

    window.mySDP = this.sdpAnswer;
    const newOne = `m=video 0 RTP/SAVPF ${this.mapNumber}
c=IN IP4 0.0.0.0
a=bundle-only
a=setup:active
a=sendonly
a=msid:stream-${random} track-${random}
a=mid:${mid}
a=rtcp-mux
a=rtcp-rsize
a=rtpmap:${this.mapNumber} VP8/90000
a=rtcp-fb:${this.mapNumber} ccm fir
a=rtcp-fb:${this.mapNumber} nack
a=rtcp-fb:${this.mapNumber} goog-remb
a=ssrc:${streamId} cname:${random}`;
    this.sdpAnswer = `${this.sdpAnswer.trim()}\n${newOne}\n`;
  }

  turnOff(streamId) {
    this.bridge_client.Switch("off", streamId);
  }

  async swapFacingMode(newFacingMode) {
    this.facingMode =
      newFacingMode ||
      (this.facingMode === "environment" ? "user" : "environment");

    this.log(`swapping facing mode to ${this.facingMode}`);

    delete this.mediaConstraints.video.deviceId; // if they've previosly specified an ID, unset it
    this.mediaConstraints.video.facingMode = { ideal: this.facingMode };
    this.log(`new constraints ${JSON.stringify(this.mediaConstraints)}`);

    // stop all tracks
    this.stream.getTracks().forEach((track) => track.stop());

    if (this.blur) {
      await stopBlurEffect(this.device_id);
    }

    setTimeout(
      () => {
        navigator.mediaDevices
          .getUserMedia(this.mediaConstraints)
          .then(async (stream) => {
            const newStream = await this.getStream(stream);
            this.stream = newStream;

            const videoTrack = newStream.getVideoTracks()[0];
            this.pc.getSenders()[0].replaceTrack(videoTrack);
            videoTrack.enabled = true;
          })
          .catch((err) => {
            this.log("error in getUserMedia");
            this.log(err);
          });
      },
      this.isAndroid ? 1000 : 0
    );
  }

  async changeResolution(resolution) {
    this.log(`changing resolution to ${resolution}`);

    this.resolution = resolution;
    this.video_width = this.resolution?.split("x")[0];
    this.video_height = this.resolution?.split("x")[1];
    this.maxBandWidth = getMaxBandwidth(
      this.video_width,
      this.video_height
    ).maxBandWidth;

    this.mediaConstraints.video.width = { ideal: this.video_width };
    this.mediaConstraints.video.height = { ideal: this.video_height };
    this.log(`new constraints ${JSON.stringify(this.mediaConstraints)}`);

    // stop all tracks
    this.stream.getTracks().forEach((track) => track.stop());

    if (this.blur) {
      await stopBlurEffect(this.device_id);
    }

    setTimeout(
      () => {
        navigator.mediaDevices
          .getUserMedia(this.mediaConstraints)
          .then(async (stream) => {
            const newStream = await this.getStream(stream);
            this.stream = newStream;

            const videoTrack = newStream.getVideoTracks()[0];
            this.pc.getSenders()[0].replaceTrack(videoTrack);
            videoTrack.enabled = true;
          })
          .catch((err) => {
            this.log("error in getUserMedia");
            this.log(err);
          });
      },
      this.isAndroid ? 1000 : 0
    );

    this.setBandwidthLimit(this.maxBandWidth);
  }

  // renegotiate bandwidth on the fly.
  async setBandwidthLimit(maxBandwidth) {
    const sender = this.pc.getSenders()[0];
    const params = sender.getParameters();
    if (!params.encodings) {
      params.encodings = [{}];
    }
    params.encodings[0].maxBitrate = maxBandwidth;
    await sender.setParameters(params);
  }

  async toggleBlur(blur = !this.blur) {
    this.log("toggleBlur");

    this.blur = blur;

    if (this.type !== "video") {
      // TODO check isStarted too?
      return;
    }

    if (!this.blur) {
      navigator.mediaDevices
        .getUserMedia(this.mediaConstraints)
        .then((stream) => {
          this.stream = stream;

          const videoTrack = stream.getVideoTracks()[0];
          this.pc.getSenders()[0].replaceTrack(videoTrack);
          videoTrack.enabled = true;
        })
        .catch((err) => {
          this.log("error in getUserMedia");
          this.log(err);
        });

      await stopBlurEffect(this.device_id);
    } else {
      const blurredStream = await this.getStream(this.stream);
      const videoTrack = this.stream.getVideoTracks()[0];
      const sender = this.pc
        .getSenders()
        .find((s) => s.track.kind === videoTrack.kind);
      sender.replaceTrack(blurredStream.getVideoTracks()[0]);
    }
  }

  // if blur is enabled, returns the blurred stream. otherwise return the normal stream
  async getStream(stream) {
    return this.blur
      ? await createBlurEffect(
          stream,
          {
            videoHeight: 640, // this.htmltag.videoHeight || 640,
            videoWidth: 480, // this.htmltag.videoWidth || 480,
          },
          this.device_id
        )
      : stream;
  }
}

function audioMeter(context) {
  const processor = context.createScriptProcessor(2048, 1, 1);
  processor.volume = 0.0;
  processor.connect(context.destination);

  processor.onaudioprocess = (event) => {
    const input = event.inputBuffer.getChannelData(0);
    let sum = 0.0;
    for (let i = 0; i < input.length; ++i) {
      sum += input[i] * input[i];
    }
    processor.volume = Math.sqrt(sum / input.length) * 300;
  };

  processor.shutdown = () => {
    processor.disconnect();
    processor.onaudioprocess = null;
  };
  return processor;
}

const IGVideoLimits = {
  VP8_SMALL: 64.0,
  VP8_MEDIUM: 256.0,
  VP8_LARGE: 768.0,
  VP8_HD1: 1024.0,
  VP8_HD2: 2048.0,
  VP8_HD3: 3072.0,
  VP8_4K_10: 4096.0,
  VP8_4K_30: 12288.0,
  // Screen shares
  VP8_SCREEN1: 256.0,
  VP8_SCREEN2: 512.0,
  VP8_SCREEN3: 1024.0,
  VP8_SCREEN4: 4096.0,
};

export const maxBandwidth = 3756;

export const resMap = [
  { res: "320x180", maxMbps: IGVideoLimits.VP8_MEDIUM },
  { res: "320x240", maxMbps: IGVideoLimits.VP8_MEDIUM },
  { res: "640x480", maxMbps: IGVideoLimits.VP8_LARGE },
  { res: "640x360", maxMbps: IGVideoLimits.VP8_LARGE },
  { res: "960x720", maxMbps: IGVideoLimits.VP8_HD1 },
  { res: "1280x720", maxMbps: IGVideoLimits.VP8_HD1 },
  { res: "1920x1080", maxMbps: IGVideoLimits.VP8_HD3 }, // maxMbps > kbps
];

export const resolutions = resMap.map((res) => res.res);

const getMaxBandwidth = (videoWidth, videoHeight) => {
  const resolution = `${videoWidth}x${videoHeight}`;
  const maxBandWidth =
    resMap[resolutions.findIndex((element) => element === resolution)]
      ?.maxMbps || maxBandwidth;
  return {
    resolution,
    maxBandWidth: maxBandWidth * 1000,
  };
};
